import { GroupAvatar } from "@/components/settings/organization/groups/GroupAvatar";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useListGroups, useListRoleAssignments } from "@/queries/group-queries";
import type { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { type FC } from "react";
import { useNavigate } from "react-router-dom";

type ResourceGroupAccessProps = {
    resourceType: ResourceType;
    resourceId: string;
    maxVisible?: number;
    className?: string;
    trackingPrefix?: string;
};

export const ResourceGroupAccess: FC<ResourceGroupAccessProps> = ({ 
    resourceType,
    resourceId, 
    maxVisible = 5, 
    className,
    trackingPrefix = "resource",
}) => {
    const navigate = useNavigate();
    const { data: roleAssignmentsData, isLoading: isLoadingAssignments } = useListRoleAssignments({
        resourceType,
        resourceId,
    });

    const { data: groupsData, isLoading: isLoadingGroups } = useListGroups();

    if (isLoadingAssignments || isLoadingGroups) {
        return null;
    }

    const assignments = roleAssignmentsData?.assignments ?? [];
    const groups = groupsData?.groups ?? [];

    // Get unique group IDs from assignments
    const groupIds = new Set(assignments.map((a) => a.groupId));

    // Find groups that have access
    const groupsWithAccess = groups.filter((g) => groupIds.has(g.id));

    if (groupsWithAccess.length === 0) {
        return null;
    }

    const visibleGroups = groupsWithAccess.slice(0, maxVisible);
    const remainingGroups = groupsWithAccess.slice(maxVisible);
    const remainingCount = remainingGroups.length;
    const showRemainingCount = remainingCount > 0;

    const handleGroupClick = (groupId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/settings/members/group/${groupId}`);
    };

    const remainingGroupsTooltip = (
        <div className="flex flex-col gap-1">
            {remainingGroups.map((g) => (
                <button
                    key={g.id}
                    type="button"
                    onClick={(e) => handleGroupClick(g.id, e)}
                    className="text-left hover:underline cursor-pointer focus:outline-none focus:underline"
                    data-tracking-id={`${trackingPrefix}-group-access-remaining-${g.id}`}
                >
                    {g.name}
                </button>
            ))}
        </div>
    );

    return (
        <div className={cn("flex items-center", className)}>
            <ul className="group/groups flex items-center" aria-label="Groups with access">
                {visibleGroups.map((group, index) => (
                    <li
                        key={group.id}
                        style={{ zIndex: maxVisible - index }}
                        className={cn(
                            "relative flex-shrink-0 transition-all ease-out",
                            index > 0 && "relative -ml-2 group-hover/groups:-ml-1",
                        )}
                    >
                        <Tooltip content={group.name} usePortal>
                            <button
                                type="button"
                                onClick={(e) => handleGroupClick(group.id, e)}
                                className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-lg"
                                aria-label={`View ${group.name} details`}
                                data-tracking-id={`${trackingPrefix}-group-access-${group.id}`}
                            >
                                <GroupAvatar
                                    groupName={group.name}
                                    size="xs"
                                    className="rounded-lg border-2 border-surface-glass ring-1 ring-border-light transition-shadow group-hover/groups:shadow-sm hover:shadow-md"
                                />
                            </button>
                        </Tooltip>
                    </li>
                ))}

                {showRemainingCount && (
                    <li className="relative -ml-2 flex-shrink-0">
                        <Tooltip content={remainingGroupsTooltip} usePortal>
                            <div
                                className={cn(
                                    "flex size-6 items-center justify-center rounded-lg border-2 border-surface-glass bg-surface-secondary text-xs font-medium text-content-secondary ring-1 ring-border-light cursor-default",
                                )}
                                aria-label={`${remainingCount} more group${remainingCount === 1 ? "" : "s"}`}
                            >
                                +{remainingCount}
                            </div>
                        </Tooltip>
                    </li>
                )}
            </ul>
        </div>
    );
};
